'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type MultiColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl,
  type SingleColumnSelectControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption } from 'echarts';

// Type for line graph specific control values
export interface LineGraphControlValues extends ControlValues {
  [ControlIds.XColumn]: string;
  [ControlIds.Values]: string[];
  [ControlIds.Title]: string;
  [ControlIds.ShowArea]: string;
  [ControlIds.AreaOpacity]: string;
  [ControlIds.MarkerSize]: string;
}

// Define the controls for the line graph
export const lineGraphControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.XColumn,
          type: ControlType.SingleColumnSelect,
          label: 'X-axis column',
          required: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Values,
          type: ControlType.MultiColumnSelect,
          label: 'Y-axis columns',
          required: true,
          numericOnly: true,
          minSelections: 1
        } satisfies MultiColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title',
          required: false
        } satisfies TextControl
      ]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.ShowArea,
          type: ControlType.Select,
          label: 'Show area',
          options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ],
          defaultValue: 'false'
        } satisfies SelectControl,
        {
          id: ControlIds.AreaOpacity,
          type: ControlType.Number,
          label: 'Area opacity',
          min: 0,
          max: 1,
          step: 0.1,
          defaultValue: 0.3
        } satisfies NumberControl,
        {
          id: ControlIds.MarkerSize,
          type: ControlType.Number,
          label: 'Marker size',
          min: 0,
          max: 20,
          step: 1,
          defaultValue: 6
        } satisfies NumberControl
      ]
    }
  ]
};

// Pure function to generate ECharts config for a line graph
export function getLineGraphEChartsConfig({
  xData,
  series,
  title,
  showArea,
  areaOpacity,
  markerSize
}: {
  xData: string[];
  series: { name: string; data: number[] }[];
  title?: string;
  showArea: boolean;
  areaOpacity: number;
  markerSize: number;
}): EChartsOption {
  return {
    title: {
      text: title || 'Line Graph',
      left: 'center',
      top: 2,
      padding: [0, 0, 0, 0]
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: series.map(s => s.name),
      top: 25
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true
    },
    toolbox: {
      feature: {
        saveAsImage: { title: 'Save' },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Reset Zoom'
          }
        }
      }
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xData,
      axisLabel: {
        rotate: xData.some(x => x.length > 10) ? 45 : 0
      }
    },
    yAxis: {
      type: 'value',
      splitLine: {
        lineStyle: {
          type: 'dashed'
        }
      }
    },
    series: series.map(s => ({
      name: s.name,
      type: 'line',
      data: s.data,
      smooth: true,
      symbol: 'circle',
      symbolSize: markerSize,
      areaStyle: showArea ? {
        opacity: areaOpacity
      } : undefined
    }))
  };
}

export const lineGraphConfig: CalculatorConfig<CalculatorId.LineChart> = {
  id: CalculatorId.LineChart,
  type: CalculatorType.Graph,
  name: 'Line Graph',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'month', 
        name: 'Month', 
        values: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] 
      },
      { 
        id: 'temperature', 
        name: 'Temperature (°C)', 
        values: ['12', '14', '16', '18', '22', '25', '28', '27', '24', '20', '16', '13'] 
      },
      {
        id: 'rainfall',
        name: 'Rainfall (mm)',
        values: ['45', '42', '50', '55', '40', '35', '30', '32', '38', '48', '52', '47']
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.XColumn]: 'month',
    [ControlIds.Values]: ['temperature', 'rainfall'],
    [ControlIds.Title]: 'Temperature and Rainfall by Month',
    [ControlIds.ShowArea]: 'false',
    [ControlIds.AreaOpacity]: '0.3',
    [ControlIds.MarkerSize]: '6'
  },
  controls: lineGraphControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );
    const nonNumericColumns = data.columns.filter(col => 
      !col.values.every(val => !isNaN(Number(val)))
    );

    return {
      [ControlIds.XColumn]: nonNumericColumns[0]?.id || data.columns[0]?.id,
      [ControlIds.Values]: numericColumns.map(col => col.id),
      [ControlIds.Title]: 'Line Graph',
      [ControlIds.ShowArea]: 'false',
      [ControlIds.AreaOpacity]: '0.3',
      [ControlIds.MarkerSize]: '6'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const lineGraphControls = controls as LineGraphControlValues;
    
    if (!data?.columns?.length || !lineGraphControls[ControlIds.Values]?.length) {
      return null;
    }

    const xColumn = data.columns.find(col => col.id === lineGraphControls[ControlIds.XColumn]);
    const selectedColumns = lineGraphControls[ControlIds.Values]
      .map(id => data.columns.find(col => col.id === id))
      .filter((col): col is typeof data.columns[0] => col !== undefined);

    if (!xColumn || !selectedColumns.length) {
      return null;
    }

    const series = selectedColumns.map(col => ({
      name: col.name,
      data: col.values.map(v => Number(v))
    }));

    return getLineGraphEChartsConfig({
      xData: xColumn.values,
      series,
      title: lineGraphControls[ControlIds.Title],
      showArea: lineGraphControls[ControlIds.ShowArea] === 'true',
      areaOpacity: Number(lineGraphControls[ControlIds.AreaOpacity]),
      markerSize: Number(lineGraphControls[ControlIds.MarkerSize])
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 